// rewardedAdService — "watch an ad → get a reward" abstraction.
//
// Web: Google H5 Games Ads (Ad Placement API). This is the ONLY way to serve
// real rewarded ads on the web with an AdSense account — standard AdSense has no
// rewarded format. It reuses the same adsbygoogle.js already loaded in
// index.html and the same publisher id. You must enable "H5 Games Ads" in your
// AdSense account for these to actually serve; until then, showRewarded()
// resolves "unavailable" and the caller applies the graceful fallback.
//
// Android (Capacitor): wire AdMob rewarded video in adMobShowRewarded() (§13).

export type RewardOutcome = "rewarded" | "dismissed" | "unavailable";

interface AdBreakOptions {
  type: "reward" | "start" | "pause" | "next" | "browse";
  name?: string;
  beforeAd?: () => void;
  afterAd?: () => void;
  beforeReward?: (showAdFn: () => void) => void;
  adDismissed?: () => void;
  adViewed?: () => void;
  adBreakDone?: (info: { breakStatus?: string }) => void;
}

declare global {
  interface Window {
    adsbygoogle: any[];
    adBreak?: (o: AdBreakOptions) => void;
    adConfig?: (o: any) => void;
  }
}

let initialised = false;

/**
 * Whether rewarded ads are enabled. Defaults to OFF so the "Watch ad" UI stays
 * hidden until Google H5 Games Ads is approved and serving on your account.
 * Flip it on by setting REACT_APP_REWARDED_ADS=true in your build environment
 * (e.g. Vercel env var), then redeploy. On native (Capacitor/AdMob) it's on.
 */
export const rewardedAdsEnabled = (): boolean => {
  if (isNative()) return true;
  return process.env.REACT_APP_REWARDED_ADS === "true";
};

/** Set up the H5 Games Ads placement API once. No-op until enabled. */
export const initRewardedAds = () => {
  if (initialised || typeof window === "undefined") return;
  if (!rewardedAdsEnabled()) return;
  initialised = true;
  try {
    window.adsbygoogle = window.adsbygoogle || [];
    // The H5 Games Ads bootstrap: adBreak and adConfig push onto adsbygoogle.
    if (!window.adBreak) {
      window.adBreak = window.adConfig = function (o: any) {
        window.adsbygoogle.push(o);
      };
    }
    window.adConfig!({
      preloadAdBreaks: "on",
      sound: "on",
      onReady: () => {},
    });
  } catch (e) {
    // H5 Games Ads not available on this account yet — fallback path handles it.
    console.warn("[rewardedAds] init failed (fallback in effect):", e);
  }
};

// Placeholder for the native path (implemented on the capacitor branch, §13).
async function adMobShowRewarded(): Promise<RewardOutcome> {
  return "unavailable";
}

const isNative = (): boolean =>
  typeof (window as any) !== "undefined" && (window as any).Capacitor?.isNativePlatform?.() === true;

/**
 * Show a rewarded ad and resolve with the outcome.
 * - "rewarded"    — the ad was fully viewed; grant the reward.
 * - "dismissed"   — the user closed the ad early; do NOT grant.
 * - "unavailable" — no ad could be shown (not enabled / no fill / error).
 *
 * The caller decides how to treat "unavailable" (we grant on fallback).
 */
export const showRewarded = (name = "reward"): Promise<RewardOutcome> => {
  if (isNative()) return adMobShowRewarded();

  return new Promise<RewardOutcome>((resolve) => {
    if (typeof window === "undefined" || typeof window.adBreak !== "function") {
      resolve("unavailable");
      return;
    }
    let settled = false;
    const done = (o: RewardOutcome) => {
      if (settled) return;
      settled = true;
      resolve(o);
    };
    // Safety timeout so the UI is never stuck waiting on the ad SDK.
    const timeout = setTimeout(() => done("unavailable"), 8000);

    try {
      window.adBreak({
        type: "reward",
        name,
        beforeReward: (showAdFn) => {
          // A preloaded reward ad is available — show it (we're in a user gesture).
          try { showAdFn(); } catch { done("unavailable"); }
        },
        adViewed: () => { clearTimeout(timeout); done("rewarded"); },
        adDismissed: () => { clearTimeout(timeout); done("dismissed"); },
        adBreakDone: (info) => {
          clearTimeout(timeout);
          // If beforeReward never fired (no ad), breakStatus reflects why.
          if (info?.breakStatus === "viewed") done("rewarded");
          else if (info?.breakStatus === "dismissed") done("dismissed");
          else done("unavailable");
        },
      });
    } catch {
      clearTimeout(timeout);
      done("unavailable");
    }
  });
};
